/**
 * Tiny procedural feedback sounds (WebAudio, no assets) + optional phone
 * vibration. Everything is guarded: audio waits for the first user gesture
 * and every call is best-effort.
 */

let ctx: AudioContext | null = null;
let unlocked = false;

/**
 * Build the audio context ahead of time, without playing anything.
 *
 * `new AudioContext()` spins up the platform's audio device and costs about
 * **260 ms of blocked main thread** — measured, on a phone profile. Doing that
 * inside the first joystick touch put a ~290 ms freeze exactly on the frame
 * the player started driving, which is the stutter that survived two rounds of
 * renderer work: every measurement had used a scripted player, and a scripted
 * player never touches the screen.
 *
 * A context may be CONSTRUCTED without a user gesture — it simply starts
 * suspended — and only resuming needs one, which costs ~0.1 ms. So the
 * expensive half happens here, during the warm-up, while the simulation is
 * held and nobody is steering.
 */
export function prepareAudio(): void {
  if (ctx) return;
  try {
    ctx = new AudioContext();
  } catch {
    /* no audio available; unlockAudio will try again on the gesture */
  }
}

/**
 * Call once from a user-gesture handler (e.g. the first joystick touch).
 * Cheap if prepareAudio() already ran, which is the point of it.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  try {
    ctx = ctx ?? new AudioContext();
    void ctx.resume();
    unlocked = true;
  } catch {
    /* no audio available */
  }
}

function blip(
  freqs: Array<[number, number]>, // [frequency Hz, duration s]
  type: OscillatorType,
  gainLevel = 0.12,
): void {
  if (!ctx || !unlocked || muted) return;
  try {
    let t = ctx.currentTime + 0.01;
    for (const [freq, dur] of freqs) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(gainLevel, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur);
      t += dur * 0.85;
    }
  } catch {
    /* best-effort */
  }
}

export function playSmooth(): void {
  blip(
    [
      [660, 0.09],
      [880, 0.12],
    ],
    "sine",
  );
  vibrate(25);
}

export function playAwkward(): void {
  blip(
    [
      [220, 0.1],
      [165, 0.16],
    ],
    "square",
    0.07,
  );
  vibrate(60);
}

export function playDogYip(): void {
  blip(
    [
      [900, 0.05],
      [1100, 0.06],
    ],
    "triangle",
    0.08,
  );
}

export function playDelivered(): void {
  blip(
    [
      [523, 0.1],
      [659, 0.1],
      [784, 0.18],
    ],
    "sine",
  );
  vibrate(40);
}

function vibrate(ms: number): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* unsupported */
  }
}

/** Vehicle horn/bell: a short two-tone for bikes, a longer low honk for cars. */
/**
 * A clean give-way just resumed (ADR-065): quiet, warm, slightly proud.
 * Softer than playSmooth — patience is praised, never startled.
 */
export function playResume(): void {
  blip(
    [
      [523, 0.07],
      [659, 0.09],
      [784, 0.14],
    ],
    "triangle",
  );
  vibrate(15);
}

/**
 * A lane star (ADR-065 amendment E). One short soft note; the pitch climbs
 * a pleasant scale with the combo and holds at the top. Deliberately
 * quieter and shorter than every other cue — it fires often.
 */
const STAR_SCALE = [523.25, 587.33, 659.25, 698.46, 783.99, 880.0];
export function playLaneStar(combo: number): void {
  const freq = STAR_SCALE[Math.min(Math.max(combo - 1, 0), STAR_SCALE.length - 1)]!;
  blip([[freq, 0.055]], "triangle");
}

export function playHorn(car: boolean): void {
  if (car) {
    blip([[220, 0.35]], "square", 0.08);
  } else {
    blip(
      [
        [1400, 0.07],
        [1400, 0.07],
      ],
      "triangle",
      0.09,
    );
  }
}


// ---------------------------------------------------------------------------
// Continuous ambience (ADR-062)
//
// The sim had event blips and nothing else: you drove a delivery robot down a
// street in total silence, which is the one sense in which it was LESS
// convincing than a video. This is a small synthesiser rather than a set of
// recordings — a loop long enough not to hear its own seam is a megabyte, and
// what a driver actually reads is not a sample but a RATE: motor pitch and
// road hiss rising together with speed. Synthesis gets that for nothing and
// tracks the speed exactly.
//
// Four voices:
//   motor   an electric whine; frequency tracks speed, so pulling away is a
//           rising note rather than a louder one
//   tyres   band-passed noise, brighter and louder the faster it rolls
//   wind    high-passed noise, growing with the square of speed
//   street  a quiet bed so a stopped robot is not in a vacuum

/** One hub motor: a body note, its electrical whine, and a shared throat. */
interface Hub {
  body: OscillatorNode;
  whine: OscillatorNode;
  whineGain: GainNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}

interface Ambience {
  master: GainNode;
  /** Left and right hub motors. A differential drive has two, and they differ. */
  hubL: Hub;
  hubR: Hub;
  tyreGain: GainNode;
  tyreFilter: BiquadFilterNode;
  windGain: GainNode;
}

let amb: Ambience | null = null;
let muted = false;

/** One second of noise, looped. Shorter is audibly periodic; longer is waste. */
function noiseSource(c: AudioContext): AudioBufferSourceNode {
  const buf = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = buf.getChannelData(0);
  // Slightly pink: plain white noise reads as radio static rather than road.
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    const w = Math.random() * 2 - 1;
    last = 0.97 * last + 0.03 * w;
    data[i] = last * 3.2;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

/** Build the ambience graph. Safe to call repeatedly; only the first builds. */
export function startAmbience(): void {
  if (amb || !ctx || !unlocked) return;
  try {
    const c = ctx;
    const master = c.createGain();
    master.gain.value = muted ? 0 : 1;
    master.connect(c.destination);

    // Two hub motors, panned apart.
    //
    // The robot is a differential drive: turning means one wheel runs faster
    // than the other, and that is audible on the real machine. A single
    // oscillator can never express it, which is why the old ambience went
    // completely quiet about steering — the stick moved and nothing changed.
    //
    // Each hub is a sawtooth body (a triangle is too pure; a BLDC has
    // harmonics) plus a separate whine an octave and a half up, which is the
    // electrical note that rises with RPM and is most of what makes a small
    // motor sound like a motor rather than a hum.
    const makeHub = (pan: number): Hub => {
      const body = c.createOscillator();
      body.type = "sawtooth";
      body.frequency.value = 62;

      const whine = c.createOscillator();
      whine.type = "triangle";
      whine.frequency.value = 62 * 6;
      const whineGain = c.createGain();
      whineGain.gain.value = 0;

      const filter = c.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 420;
      filter.Q.value = 1.6;

      const gain = c.createGain();
      gain.gain.value = 0;

      body.connect(filter);
      whine.connect(whineGain).connect(filter);
      filter.connect(gain);

      // StereoPanner is not everywhere; without it both hubs still work, they
      // just share the centre.
      if (typeof c.createStereoPanner === "function") {
        const p = c.createStereoPanner();
        p.pan.value = pan;
        gain.connect(p).connect(master);
      } else {
        gain.connect(master);
      }

      body.start();
      whine.start();
      return { body, whine, whineGain, filter, gain };
    };
    const hubL = makeHub(-0.35);
    const hubR = makeHub(0.35);

    const tyres = noiseSource(c);
    const tyreFilter = c.createBiquadFilter();
    tyreFilter.type = "bandpass";
    tyreFilter.frequency.value = 500;
    tyreFilter.Q.value = 0.7;
    const tyreGain = c.createGain();
    tyreGain.gain.value = 0;
    tyres.connect(tyreFilter).connect(tyreGain).connect(master);
    tyres.start();

    const wind = noiseSource(c);
    const windFilter = c.createBiquadFilter();
    windFilter.type = "highpass";
    windFilter.frequency.value = 2200;
    const windGain = c.createGain();
    windGain.gain.value = 0;
    wind.connect(windFilter).connect(windGain).connect(master);
    wind.start();

    // The street itself: low, constant, never zero.
    const street = noiseSource(c);
    const streetFilter = c.createBiquadFilter();
    streetFilter.type = "lowpass";
    streetFilter.frequency.value = 320;
    const streetGain = c.createGain();
    streetGain.gain.value = 0.035;
    street.connect(streetFilter).connect(streetGain).connect(master);
    street.start();

    amb = { master, hubL, hubR, tyreGain, tyreFilter, windGain };
  } catch {
    /* no audio available */
  }
}

/**
 * Drive the ambience from the robot AND from the stick, every frame.
 *
 * The old version listened to `speed` alone, at ~7 Hz, and that is why the
 * sound felt disconnected from the controls: the robot takes about a second
 * to reach cruise, so pushing the stick produced silence until the machine
 * had already gone. Worse, steering was completely inaudible, and pushing
 * against a kerb — full command, zero speed — sounded exactly like coasting,
 * which is the one moment a real motor is loudest.
 *
 * So three signals, not one:
 *
 * - **rpm** (from speed) sets the pitch. Physical, and unchanged.
 * - **load** = how much more the driver is asking for than the machine is
 *   delivering. It rises the instant the stick moves, and stays high while
 *   the robot strains against something. Load opens the filter, lifts the
 *   whine and adds gain: the sound of effort.
 * - **steer** splits the two hubs. On a differential drive the outer wheel
 *   genuinely runs faster, so a turn is heard as the two notes parting.
 *
 * `setTargetAtTime` rather than direct assignment: stepping a gain every
 * frame is a click every frame, and the short time constants here are what
 * make the whole thing sound like one machine instead of five.
 *
 * @param speedMps  what the robot is actually doing
 * @param cruiseMps the level's cruise speed, as the scale for "fast"
 * @param throttle  commanded effort, 0..1 (stick magnitude)
 * @param steer     commanded turn, -1 (left) .. 1 (right)
 * @param reversing true when the command is backwards
 */
/** What one hub motor should be doing this frame. */
export interface HubVoice {
  hz: number;
  whineHz: number;
  whineGain: number;
  cutoffHz: number;
  gain: number;
}

/** The whole ambience, as numbers. */
export interface DriveVoice {
  left: HubVoice;
  right: HubVoice;
  tyreGain: number;
  tyreHz: number;
  windGain: number;
  /** How hard the machine is being asked to work, 0..1. Exposed for tests. */
  load: number;
}

/**
 * The mapping from what the robot is doing and what the driver is asking for,
 * to what the ambience should sound like. Pure arithmetic on purpose: WebAudio
 * cannot be asserted on in a unit test, but this can, and this is where every
 * complaint about the sound actually lives.
 */
export function driveVoice(
  speedMps: number,
  cruiseMps: number,
  throttle = 0,
  steer = 0,
  reversing = false,
): DriveVoice {
  // A NaN reaching an AudioParam throws and kills the ambience for the rest
  // of the session, so nothing gets past this line unclamped.
  const num = (x: number, fallback = 0): number => (Number.isFinite(x) ? x : fallback);
  const cruise = Math.max(num(cruiseMps, 1), 0.5);
  const v = Math.max(0, Math.min(1.4, num(speedMps) / cruise));
  const cmd = Math.max(0, Math.min(1, num(throttle)));
  const turn = Math.max(-1, Math.min(1, num(steer)));

  // Asking for more than the machine is giving. Pulling away scores high for
  // about a second; shoving a kerb scores high until the stick is released.
  const load = Math.max(0, Math.min(1, cmd - v * 0.85));

  // A differential drive: the outer wheel turns faster. A third of the stick's
  // lateral throw is enough to hear without turning a corner into a siren.
  const split = turn * 0.32 * (0.35 + 0.65 * cmd);

  // Reverse gearing on a hub motor sits a little lower and rougher.
  const dirK = reversing ? 0.86 : 1;

  // `side` is -1 for the left hub, +1 for the right. Turning right (positive
  // steer) puts the LEFT wheel on the outside of the arc, so it is the left
  // one that speeds up — hence the minus.
  const hub = (side: number): HubVoice => {
    const wheel = Math.max(0, v * (1 - side * split) + cmd * 0.18);
    const hz = (58 + 172 * wheel + 26 * load) * dirK;
    return {
      hz,
      // A high multiple of the same note, so the whine tracks RPM without
      // ever beating against the body.
      whineHz: hz * 6.2,
      whineGain: 0.06 * wheel + 0.1 * load,
      // Effort opens the throat: a loaded motor is brighter, not just louder.
      cutoffHz: 360 + 900 * wheel + 1200 * load,
      gain: wheel > 0.02 || load > 0.02 ? 0.028 + 0.03 * wheel + 0.035 * load : 0,
    };
  };

  return {
    left: hub(-1),
    right: hub(1),
    tyreGain: 0.1 * v,
    tyreHz: 380 + 900 * v,
    windGain: 0.05 * v * v,
    load,
  };
}

export function setDrive(
  speedMps: number,
  cruiseMps: number,
  throttle = 0,
  steer = 0,
  reversing = false,
): void {
  if (!amb || !ctx) return;
  try {
    const t = ctx.currentTime;
    const d = driveVoice(speedMps, cruiseMps, throttle, steer, reversing);
    for (const [hub, want] of [
      [amb.hubL, d.left],
      [amb.hubR, d.right],
    ] as Array<[Hub, HubVoice]>) {
      hub.body.frequency.setTargetAtTime(want.hz, t, 0.05);
      hub.whine.frequency.setTargetAtTime(want.whineHz, t, 0.05);
      hub.whineGain.gain.setTargetAtTime(want.whineGain, t, 0.06);
      hub.filter.frequency.setTargetAtTime(want.cutoffHz, t, 0.07);
      hub.gain.gain.setTargetAtTime(want.gain, t, 0.08);
    }
    amb.tyreGain.gain.setTargetAtTime(d.tyreGain, t, 0.07);
    amb.tyreFilter.frequency.setTargetAtTime(d.tyreHz, t, 0.1);
    amb.windGain.gain.setTargetAtTime(d.windGain, t, 0.12);
  } catch {
    /* best effort */
  }
}

/**
 * The click a control makes. Short, quiet, and pitched above the motor so it
 * cuts through without being a beep.
 *
 * The HUD buttons were silent: a tap either did something visible or left the
 * player wondering whether the press had registered at all, which on a phone
 * held one-handed while driving is most of the time.
 */
export function playTick(kind: "on" | "off" = "on"): void {
  blip([[kind === "on" ? 880 : 620, 0.035]], "sine", 0.05);
  vibrate(8);
}

export function setMuted(on: boolean): void {
  muted = on;
  if (!amb || !ctx) return;
  try {
    amb.master.gain.setTargetAtTime(on ? 0 : 1, ctx.currentTime, 0.05);
  } catch {
    /* best effort */
  }
}

export function isMuted(): boolean {
  return muted;
}
